import { type CSSProperties } from 'react';

import { getFromBreakpoint } from '@vakantiesnl/components/src/styles/breakpoints';
import { makeStyles } from '@vakantiesnl/components/src/styles/themes/themewrapper';

/**
 * This grid template enables a flexible grid
 * where the key is the amount of images to show
 **/
const gridTemplate: Record<number, CSSProperties['gridTemplateAreas']> = {
	1: `'first first' 'first first'`,
	2: `'first first' 'second second'`,
	3: `'first second' 'third third'`,
	4: `'first second' 'third fourth'`,
};

export const useCustomStyles = makeStyles<{ imagesCount: number }>()((theme, { imagesCount }) => ({
	container: {
		// isolate stacking context to not overlap another elements with lower z-index
		// for example - sticky header
		isolation: 'isolate',
		display: 'grid',
		gridTemplateRows: theme.gallery.height.sm,

		[getFromBreakpoint('md')]: {
			gridTemplateRows: theme.gallery.height.md,
			gridTemplateColumns: imagesCount > 0 ? '2fr 1fr' : 'auto',
			gap: theme.spacing.xxsmall,
		},
	},
	image: {
		position: 'relative',
		objectFit: 'cover',
		[getFromBreakpoint('md')]: {
			borderRadius: theme.borderRadius.rounded,
		},
	},
	mainImage: { position: 'relative' },
	detailImages: {
		display: 'grid',
		gridTemplateAreas: gridTemplate[imagesCount],
		gridTemplateRows: '1fr 1fr',
		gap: theme.spacing.xxsmall,
	},
	detailImage: {
		maxHeight: '100%',
		width: '100%',
		height: '100%',
		overflow: 'hidden',

		/** Supporting flexible css grid layout for multiple conditions */
		'&1': {
			gridArea: 'first',
		},
		'&2': {
			gridArea: 'second',
		},
		'&3': {
			gridArea: 'third',
		},
		'&4': {
			gridArea: 'fourth',
		},
	},
	ratingContainer: {
		zIndex: theme.zIndex.lifted,
		padding: theme.spacing.xxsmall,
		position: 'absolute',
		background: theme.gallery.foreground.color,
		top: theme.spacing.xxsmall,
		left: theme.spacing.xxsmall,
		borderRadius: theme.borderRadius.rounded,
		[getFromBreakpoint('md')]: {
			top: theme.spacing.small,
			left: theme.spacing.small,
		},
	},
	tagContainer: {
		zIndex: theme.zIndex.lifted,
		position: 'absolute',
		bottom: theme.spacing.small,
		right: theme.spacing.small,
	},
	overlay: {
		position: 'absolute',
		top: 0,
		width: '100%',
		height: '100%',
		zIndex: theme.zIndex.lifted,
		background: theme.gallery.curtain.color,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	imagesCount: {
		color: theme.gallery.foreground.color,
		zIndex: theme.zIndex.lifted,
		fontSize: theme.fontSize.xLarge,
		fontWeight: theme.weight.bold,

		'&:before': {
			content: '"+"',
		},
	},
	clickable: {
		cursor: 'pointer',
	},
	overlayComponentContainer: {
		zIndex: theme.zIndex.lifted,
		position: 'absolute',
		top: theme.spacing.small,
		right: theme.spacing.small,
	},
}));
